import {
  API_DATE_FORMAT,
  LeaveRequestStatus,
  ShiftRequestErrorId,
  UnavailabilityRecordType,
  UnavailabilityStatus,
} from '$constants'
import {
  CoreApiError,
  ILeaveRequest,
  IUnavailabilityRecord,
  ShiftRequest,
} from '$models'
import {
  leaveRequestService,
  shiftRequestService,
  unavailabilityService,
} from '$services'
import { enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import { Dayjs } from 'dayjs'
import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'

/** Conflicts we expect. The backend message says enough on its own, so no error id is shown. */
const expectedResolveConflictErrorIds = new Set<string>([
  ShiftRequestErrorId.NotEligible,
  ShiftRequestErrorId.ShiftUnavailable,
  ShiftRequestErrorId.ShiftAlreadyStarted,
  ShiftRequestErrorId.CounterShiftNotFound,
  ShiftRequestErrorId.CounterShiftNotOwned,
  ShiftRequestErrorId.InitiatorNotEligibleForCounter,
  ShiftRequestErrorId.CounterShiftAlreadyStarted,
  ShiftRequestErrorId.RequestNotFound,
  ShiftRequestErrorId.InvalidUser,
  ShiftRequestErrorId.NotInitiator,
  ShiftRequestErrorId.NotRecipient,
  ShiftRequestErrorId.UnsupportedStatus,
])

/**
 * Handles an error from a manager approve or decline. A request someone else already resolved gets
 * refreshed; unexpected errors also show the error id, which is logged in New Relic.
 */
const handleShiftRequestResolveError = async (
  error: unknown,
  shiftRequestId: string,
  refresh: (id: string) => Promise<ShiftRequest>
): Promise<void> => {
  if (
    error instanceof CoreApiError &&
    error.id === ShiftRequestErrorId.StatusConflict
  ) {
    enqueueLoadedSnackbar('This request has already been resolved', 'warning')
    try {
      await refresh(shiftRequestId)
    } catch (refreshError) {
      // The refresh has its own failure modes (e.g. the network dropped right after the
      // conflict). Report it instead of letting the rejection escape the caller's catch.
      exceptionMessageHandler(refreshError)
    }
    return
  }

  if (error instanceof CoreApiError) {
    if (error.id && expectedResolveConflictErrorIds.has(error.id)) {
      enqueueLoadedSnackbar(
        error.messages?.[0] ?? 'The operation has failed',
        'error'
      )
      return
    }

    enqueueLoadedSnackbar(
      error.messages?.[0] ?? error.description ?? 'The operation has failed',
      'error',
      [],
      error.id,
      error.code
    )
    return
  }

  exceptionMessageHandler(error)
}

interface ManageRequestState {
  leaveRequest: ILeaveRequest | null
  unavailabilityRequest: IUnavailabilityRecord | null
  shiftRequest: ShiftRequest | null
  overlappingLeaveRequests: ILeaveRequest[]
  overlappingOneOffUnavailabilityRecords: IUnavailabilityRecord[]
}

export interface ManageRequestSlice extends ManageRequestState {
  /**
   * Gets a Leave Request by its Id.
   * @param id the Id of the Leave Request
   */
  getLeaveRequest: (id: string) => Promise<ILeaveRequest>

  /**
   * Updates the status of a Leave Request.
   * @param leaveRequestId the Id of the Leave Request
   * @param status the status to update the Leave Request to
   */
  updateManageRequestLeaveRequestStatus: (
    leaveRequestId: string,
    status: LeaveRequestStatus
  ) => Promise<ILeaveRequest>

  /**
   * Gets a Shift Request by its Id.
   * @param id the Id of the Shift Request
   */
  getShiftRequest: (id: string) => Promise<ShiftRequest>

  /**
   * Approves a Shift Request on the manager's behalf; the shift moves to the recipient. Shows the
   * outcome and refreshes the detail when someone else already resolved the request.
   * @param shiftRequestId the Id of the Shift Request
   * @param note optional manager note stored on the approval
   */
  approveShiftRequest: (shiftRequestId: string, note?: string) => Promise<void>

  /**
   * Declines a Shift Request on the manager's behalf; the shift stays with the initiator. Shows the
   * outcome and refreshes the detail when someone else already resolved the request.
   * @param shiftRequestId the Id of the Shift Request
   * @param note optional manager note stored on the decline
   */
  declineShiftRequest: (shiftRequestId: string, note?: string) => Promise<void>

  /**
   * Gets an Unavailability Request by its Id.
   * @param id the Id of the Unavailability Request
   */
  getUnavailabilityRequest: (id: string) => Promise<IUnavailabilityRecord>

  /**
   * Updates the status of an Unavailability Request.
   * @param unavailabilityRequestId the Id of the Unavailability Request
   * @param status the status to update the Unavailability Request to
   */
  updateUnavailabilityRequestStatus: (
    unavailabilityRequestId: string,
    status: UnavailabilityStatus
  ) => Promise<IUnavailabilityRecord>

  /**
   * Gets the overlapping leave and unavailability within a given period
   * @param from the start of the date period
   * @param to the end of the date period
   */
  getOverlappingLeaveAndUnavailability: (
    from: Dayjs,
    to: Dayjs
  ) => Promise<void>
}

const initialState: ManageRequestState = {
  leaveRequest: null,
  unavailabilityRequest: null,
  shiftRequest: null,
  overlappingLeaveRequests: [],
  overlappingOneOffUnavailabilityRecords: [],
}

const createManageRequestSlice: StateCreator<
  ManageRequestSlice & SharedSlice,
  [],
  [],
  ManageRequestSlice
> = (set, get) => ({
  ...initialState,

  getLeaveRequest: async (leaveRequestId: string) => {
    const leaveRequest = await leaveRequestService.getById(leaveRequestId)
    set(() => ({ leaveRequest }))
    return leaveRequest
  },

  updateManageRequestLeaveRequestStatus: async (
    leaveRequestId: string,
    status: LeaveRequestStatus
  ) => {
    const updatedLeaveRequest = await leaveRequestService.updateStatus(
      leaveRequestId,
      status
    )
    set(() => ({
      leaveRequest: updatedLeaveRequest,
    }))
    return updatedLeaveRequest
  },

  getShiftRequest: async (shiftRequestId: string) => {
    const shiftRequest = await shiftRequestService.getById(shiftRequestId)
    set(() => ({ shiftRequest }))
    return shiftRequest
  },

  approveShiftRequest: async (shiftRequestId: string, note?: string) => {
    try {
      const shiftRequest = await shiftRequestService.approve(
        shiftRequestId,
        note
      )
      set(() => ({ shiftRequest }))
      enqueueLoadedSnackbar('Shift request approved', 'success')
    } catch (error) {
      await handleShiftRequestResolveError(
        error,
        shiftRequestId,
        get().getShiftRequest
      )
    }
  },

  declineShiftRequest: async (shiftRequestId: string, note?: string) => {
    try {
      const shiftRequest = await shiftRequestService.decline(
        shiftRequestId,
        note
      )
      set(() => ({ shiftRequest }))
      enqueueLoadedSnackbar('Shift request declined', 'success')
    } catch (error) {
      await handleShiftRequestResolveError(
        error,
        shiftRequestId,
        get().getShiftRequest
      )
    }
  },

  getUnavailabilityRequest: async (unavailabilityRequestId: string) => {
    const unavailabilityRequest = await unavailabilityService.getById(
      unavailabilityRequestId
    )
    set(() => ({ unavailabilityRequest }))
    return unavailabilityRequest
  },

  updateUnavailabilityRequestStatus: async (
    unavailabilityRequestId: string,
    status: UnavailabilityStatus
  ) => {
    const updatedUnavailabilityRequest =
      await unavailabilityService.updateStatus(unavailabilityRequestId, status)
    set(() => ({
      unavailabilityRequest: updatedUnavailabilityRequest,
    }))
    return updatedUnavailabilityRequest
  },

  getOverlappingLeaveAndUnavailability: async (from: Dayjs, to: Dayjs) => {
    const fromFormatted = from.format(API_DATE_FORMAT)
    // As weekly unavailability can have null as the end date, if to is invalid
    // we can assume this is the case
    const toFormatted = to.isValid() ? to.format(API_DATE_FORMAT) : undefined

    const leaveRequests = leaveRequestService.getAll(
      undefined,
      fromFormatted,
      toFormatted,
      [LeaveRequestStatus.Requested, LeaveRequestStatus.Approved]
    )

    const unavailability = unavailabilityService.getAll(
      undefined,
      fromFormatted,
      toFormatted,
      UnavailabilityRecordType.OneOff,
      [UnavailabilityStatus.Requested, UnavailabilityStatus.Approved]
    )

    const requests = await Promise.all([leaveRequests, unavailability])

    set(() => ({
      overlappingLeaveRequests: requests[0],
      overlappingOneOffUnavailabilityRecords: requests[1],
    }))
  },
})

export default createManageRequestSlice
